import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { 
  ArrowLeft, ThumbsUp, MessageCircle, Flag, Trash2, 
  Loader2, Send, Lock, SmilePlus 
} from 'lucide-react';
import {
  getThread,
  getReplies,
  createReply,
  toggleThreadLike,
  toggleReplyLike,
  deleteThread,
  deleteReply,
  reportContent,
} from '../Services/ForumService';
import { threadFollowService } from '../Services/threadFollowService';
import { useAuth } from '../contexts/AuthContext';
import { formatTimeAgo } from '../Services/utils/dateHelpers';
import FollowButton from '../components/forum/FollowButton';

const ThreadDetailPage = () => {
  const { threadId } = useParams();
  const { currentUser: user } = useAuth(); // Vérifie si c'est user ou currentUser dans ton contexte
  const navigate = useNavigate();

  const [thread, setThread] = useState(null);
  const [replies, setReplies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [replyContent, setReplyContent] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadThreadAndReplies();
  }, [threadId]);

  const loadThreadAndReplies = async () => {
    try {
      setLoading(true);
      const [threadData, repliesData] = await Promise.all([
        getThread(threadId),
        getReplies(threadId),
      ]);
      setThread(threadData);
      setReplies(repliesData);
    } catch (error) {
      console.error('Erreur chargement:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitReply = async (e) => {
    e.preventDefault();
    if (!user) { alert('Connectez-vous pour répondre'); return; }
    if (!replyContent.trim() || replyContent.length < 10) {
      alert('Votre réponse doit faire au moins 10 caractères');
      return;
    }

    try {
      setSubmitting(true);
      await createReply(threadId, user.uid, replyContent);
      try {
        await threadFollowService.createNotification(threadId, user.uid, thread.title);
      } catch (notifError) {
        console.error('Erreur notifications:', notifError);
      }
      setReplyContent('');
      await loadThreadAndReplies();
    } catch (error) {
      alert("Erreur lors de l'envoi");
    } finally {
      setSubmitting(false);
    }
  };

  const handleLikeThread = async () => {
    if (!user) { alert('Connectez-vous pour liker'); return; }
    try {
      await toggleThreadLike(threadId, user.uid);
      await loadThreadAndReplies();
    } catch (error) {
      console.error('Erreur like:', error);
    }
  };

  const handleLikeReply = async (replyId) => {
    if (!user) { alert('Connectez-vous pour liker'); return; }
    try {
      await toggleReplyLike(replyId, user.uid);
      await loadThreadAndReplies();
    } catch (error) {
      console.error('Erreur like reply:', error);
    }
  };

  const handleDeleteThread = async () => {
    if (!window.confirm('Supprimer ce sujet et toutes ses réponses ?')) return;
    try {
      await deleteThread(threadId, user.uid);
      navigate('/forum');
    } catch (error) {
      alert('Erreur lors de la suppression');
    }
  };

  const handleDeleteReply = async (replyId) => {
    if (!window.confirm('Supprimer cette réponse ?')) return;
    try {
      await deleteReply(replyId, threadId, user.uid);
      await loadThreadAndReplies();
    } catch (error) {
      alert('Erreur lors de la suppression');
    }
  };

  const handleReport = async (type, itemId) => {
    if (!user) { alert('Connectez-vous pour signaler'); return; }
    const reason = prompt('Raison (1: Inapproprié, 2: Médical, 3: Insulte, 4: Spam):');
    if (!reason) return;
    const reasons = { 1: 'Inapproprié', 2: 'Désinformation médicale', 3: 'Insultes', 4: 'Spam' };
    try {
      await reportContent(type, itemId, user.uid, reasons[reason] || 'Autre', '');
      alert('Signalé aux modérateurs');
    } catch (error) {
      alert('Erreur signalement');
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex flex-col justify-center items-center py-24 space-y-4">
          <Loader2 className="w-12 h-12 animate-spin text-blue-500" />
          <p className="text-gray-500 animate-pulse font-medium">Ouverture de la discussion...</p>
        </div>
      </DashboardLayout>
    );
  }

  if (!thread) {
    return (
      <DashboardLayout>
        <div className="max-w-3xl mx-auto px-4 py-12 text-center">
          <Card className="p-12 border-dashed border-2">
            <p className="text-gray-500 mb-6">Ce sujet n'existe plus.</p>
            <Button onClick={() => navigate('/forum')}>Retour au forum</Button>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="min-h-screen bg-gray-50/30 dark:bg-[#0a0a0b] pb-20">
        <div className="max-w-4xl mx-auto px-4 py-8">
          
          <div className="flex items-center justify-between mb-8">
            <button onClick={() => navigate('/forum')} className="group flex items-center text-sm font-medium text-gray-500 hover:text-blue-600 transition-colors">
              <div className="p-2 mr-2 rounded-full group-hover:bg-blue-50 dark:group-hover:bg-blue-900/20 transition-all">
                <ArrowLeft className="w-4 h-4" />
              </div>
              Retour au forum
            </button>
            <FollowButton threadId={threadId} />
          </div>

          <article className="bg-white dark:bg-[#121214] rounded-3xl shadow-sm border border-gray-100 dark:border-gray-800 p-6 md:p-10 mb-10">
            <header className="mb-8">
              <h1 className="text-3xl md:text-4xl font-extrabold text-gray-900 dark:text-white leading-tight mb-6">{thread.title}</h1>
              <div className="flex items-center gap-4 p-4 rounded-2xl bg-gray-50 dark:bg-gray-800/40 border border-gray-100 dark:border-gray-800">
                <div className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg" style={{ backgroundColor: thread.authorAvatar || '#3b82f6' }}>
                  {thread.authorPseudo?.charAt(0).toUpperCase()}
                </div>
                <div>
                  <div className="font-bold text-gray-900 dark:text-white">{thread.authorPseudo}</div>
                  <div className="text-xs text-gray-500 font-semibold uppercase tracking-wider">Publié {formatTimeAgo(thread.createdAt)}</div>
                </div>
              </div>
            </header>

            <div className="prose dark:prose-invert max-w-none text-lg text-gray-700 dark:text-gray-300 mb-10">{thread.content}</div>

            <footer className="flex items-center justify-between pt-6 border-t border-gray-100 dark:border-gray-800">
              <div className="flex items-center gap-4">
                <button onClick={handleLikeThread} className={`flex items-center gap-2 px-5 py-2.5 rounded-full font-bold transition-all ${thread.likedBy?.includes(user?.uid) ? 'bg-blue-600 text-white shadow-lg' : 'bg-gray-100 dark:bg-gray-800 text-gray-600'}`}>
                  <ThumbsUp className="w-4 h-4" /> {thread.likes || 0}
                </button>
                <div className="flex items-center gap-2 text-gray-400 font-medium px-4">
                  <MessageCircle className="w-5 h-5" /> {thread.repliesCount || 0}
                </div>
              </div>
              <div className="flex gap-2">
                {user?.uid === thread.authorId && <Button variant="ghost" size="icon" onClick={handleDeleteThread} className="text-red-500"><Trash2 className="w-5 h-5" /></Button>}
                <Button variant="ghost" size="icon" onClick={() => handleReport('thread', thread.id)} className="text-gray-400"><Flag className="w-5 h-5" /></Button>
              </div>
            </footer>
          </article>

          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Réponses <span className="ml-2 text-sm font-normal px-3 py-1 bg-gray-200 dark:bg-gray-800 rounded-full text-gray-500">{replies.length}</span></h2>

            {thread.isLocked ? (
              <div className="p-6 bg-gray-100 dark:bg-gray-800/50 rounded-2xl text-center text-gray-500 italic">Discussion verrouillée.</div>
            ) : (
              <Card className="p-2 bg-white dark:bg-[#121214] border-gray-200 dark:border-gray-800 rounded-2xl shadow-sm">
                <form onSubmit={handleSubmitReply}>
                  <textarea value={replyContent} onChange={(e) => setReplyContent(e.target.value)} placeholder="Écrivez votre réponse..." className="w-full p-4 bg-transparent border-none focus:ring-0 text-gray-800 dark:text-gray-200 min-h-[120px] resize-none text-lg" disabled={submitting || !user} />
                  <div className="flex items-center justify-between p-3 border-t border-gray-50 dark:border-gray-800">
                    <div className="flex items-center gap-1 overflow-x-auto no-scrollbar">
                      {['❤️', '💪', '👍', '🙏', '😊', '🌟'].map(emoji => (
                        <button key={emoji} type="button" onClick={() => setReplyContent(prev => prev + emoji)} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-transform active:scale-125">{emoji}</button>
                      ))}
                    </div>
                    <Button type="submit" disabled={submitting || !user} className="rounded-xl bg-blue-600 hover:bg-blue-700 px-6">
                      {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-2" /> Répondre</>}
                    </Button>
                  </div>
                </form>
              </Card>
            )}

            <div className="relative mt-12 space-y-6">
              {replies.length > 0 && <div className="absolute left-7 top-0 bottom-0 w-0.5 bg-gradient-to-b from-gray-200 to-transparent dark:from-gray-800 hidden md:block" />}
              {replies.map((reply) => (
                <div key={reply.id} className="relative group pl-0 md:pl-14">
                  <div className="bg-white dark:bg-[#121214] rounded-2xl border border-gray-100 dark:border-gray-800 p-5 shadow-sm hover:shadow-md transition-all">
                    <header className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full flex items-center justify-center text-white font-bold text-xs shadow-sm" style={{ backgroundColor: reply.authorAvatar || '#64748b' }}>
                          {reply.authorPseudo?.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <span className="font-bold text-gray-900 dark:text-gray-100 text-sm">{reply.authorPseudo}</span>
                          <span className="mx-2 text-gray-300">•</span>
                          <span className="text-xs text-gray-400">{formatTimeAgo(reply.createdAt)}</span>
                        </div>
                      </div>
                      <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                         {user?.uid === reply.authorId ? (
                           <button onClick={() => handleDeleteReply(reply.id)} className="p-2 text-red-400 hover:bg-red-50 rounded-full transition-colors"><Trash2 className="w-4 h-4" /></button>
                         ) : (
                           <button onClick={() => handleReport('reply', reply.id)} className="p-2 text-gray-400 hover:bg-gray-100 rounded-full"><Flag className="w-4 h-4" /></button>
                         )}
                      </div>
                    </header>
                    <div className="text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-wrap pl-1">{reply.content}</div>
                    <div className="mt-4 flex items-center gap-4">
                      <button onClick={() => handleLikeReply(reply.id)} className={`flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full transition-all ${reply.likedBy?.includes(user?.uid) ? 'bg-pink-50 text-pink-600' : 'text-gray-400 hover:bg-gray-50'}`}>
                        <ThumbsUp className="w-3.5 h-3.5" /> {reply.likes || 0}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              {replies.length === 0 && (
                <div className="py-20 text-center">
                  <SmilePlus className="w-12 h-12 text-gray-200 mx-auto mb-4" />
                  <p className="text-gray-400 font-medium">Soyez le premier à contribuer !</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default ThreadDetailPage;